/**
 * The team deletes that run in the background (TRA-1508), as the Teams
 * lists follow them: the owner's (GET /api/settings/team-deletions, the
 * teams the caller owns) and the staff one (GET /api/internal/team-deletions,
 * every team). Both read again every DELETIONS_POLL_MS while a delete is
 * queued or running and stop once none is; a Delete click or a Retry
 * reads them again at once.
 *
 * The page watches every delete it has seen queued or running, and the
 * one its own click answered with (`started`): a small team's delete can
 * be over before the first read after the click, and that read must
 * still count as the finish. Every fresh read is compared with the
 * watched deletes, by the query's own read time rather than the list's
 * identity, since a read that finds the same list again is still a read.
 * `onDone` fires once per watched delete that is done in a read,
 * `onVanished` once per watched delete that a read no longer lists; the
 * page decides what each means for its team list. The owner's list never
 * holds a done delete (the team's group goes in the last step, and the
 * list drops a team whose group is gone), so the owner's page has only
 * `onVanished`.
 */

import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryKey, type UseMutationResult } from '@tanstack/react-query'

import { getApiErrorDetail, type ApiRequestError } from '@/api/client'
import {
  getListTeamDeletionsApiInternalTeamDeletionsGetQueryKey,
  useListTeamDeletionsApiInternalTeamDeletionsGet,
  useRetryTeamDeletionApiInternalTeamDeletionsDeletionIdRetryPost,
} from '@/api/generated/internal/internal'
import type { TeamDeleteRecord } from '@/api/generated/model'
import {
  getListTeamDeletionsApiSettingsTeamDeletionsGetQueryKey,
  useListTeamDeletionsApiSettingsTeamDeletionsGet,
  useRetryTeamDeletionApiSettingsTeamDeletionsDeletionIdRetryPost,
} from '@/api/generated/settings/settings'
import {
  activeById,
  justFinished,
  justVanished,
  nextDeletionsPollMs,
  recordsByTeam,
  teamDeleteState,
  type RetryError,
  type TeamDeleteRowState,
} from '@/components/shared/teamDeletions'

export interface TeamDeletions {
  /** What the team's row shows for its latest delete. */
  stateOf: (teamId: string) => TeamDeleteRowState
  /** The Delete click answered with this record: watch it, and read the records again now. */
  started: (record: TeamDeleteRecord) => void
  /** Queue a failed delete again; it continues from the step it failed on. */
  retry: (deletionId: string) => void
}

interface TeamDeletionsOptions {
  /** A watched delete is done. Staff only: the owner's list never shows one. */
  onDone?: (record: TeamDeleteRecord) => void
  /** A watched delete left the list (the record as last seen). */
  onVanished: (record: TeamDeleteRecord) => void
}

interface Read {
  records: readonly TeamDeleteRecord[] | undefined
  /** When the records were read (the query's `dataUpdatedAt`); 0 before the first read. */
  at: number
}

const RETRY_FAILED = 'The delete could not be retried.'

function useWatched(
  read: Read,
  { onDone, onVanished }: TeamDeletionsOptions,
): (record: TeamDeleteRecord) => void {
  const watched = useRef<ReadonlyMap<string, TeamDeleteRecord>>(new Map())
  const lastReadAt = useRef(0)
  // A new callback closure re-runs this; the read is then the one already
  // compared, and nothing fires twice.
  useEffect(() => {
    if (read.records === undefined || read.at === lastReadAt.current) return
    lastReadAt.current = read.at
    if (onDone !== undefined) {
      for (const record of justFinished(watched.current, read.records)) onDone(record)
    }
    for (const record of justVanished(watched.current, read.records)) onVanished(record)
    watched.current = activeById(read.records)
  }, [read, onDone, onVanished])
  return (record) => {
    watched.current = new Map([...watched.current, [record.id, record]])
  }
}

function useDeletions(
  read: Read,
  queryKey: QueryKey,
  retry: UseMutationResult<unknown, ApiRequestError, { deletionId: string }>,
  options: TeamDeletionsOptions,
): TeamDeletions {
  const queryClient = useQueryClient()
  const watch = useWatched(read, options)
  const byTeam = recordsByTeam(read.records ?? [])
  const retryError: RetryError | null = retry.isError
    ? { deletionId: retry.variables.deletionId, message: getApiErrorDetail(retry.error) ?? RETRY_FAILED }
    : null
  return {
    stateOf: (teamId) => teamDeleteState(byTeam.get(teamId), retryError),
    started: (record) => {
      watch(record)
      void queryClient.invalidateQueries({ queryKey })
    },
    retry: (deletionId) => {
      retry.mutate({ deletionId })
    },
  }
}

function useRefreshAfter(queryKey: QueryKey): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey })
  }
}

/** The owner's view: the deletes of the teams the caller owns. */
export function useOwnerTeamDeletions(options: TeamDeletionsOptions): TeamDeletions {
  const queryKey = getListTeamDeletionsApiSettingsTeamDeletionsGetQueryKey()
  const query = useListTeamDeletionsApiSettingsTeamDeletionsGet({
    query: {
      staleTime: 0,
      retry: false,
      refetchInterval: (current) => {
        const body = current.state.data
        return nextDeletionsPollMs(body?.status === 200 ? body.data.deletions : undefined)
      },
    },
  })
  const retry = useRetryTeamDeletionApiSettingsTeamDeletionsDeletionIdRetryPost({
    mutation: { onSettled: useRefreshAfter(queryKey) },
  })
  const read: Read = {
    records: query.data?.status === 200 ? query.data.data.deletions : undefined,
    at: query.dataUpdatedAt,
  }
  return useDeletions(read, queryKey, retry, options)
}

/** The staff view: the latest delete of every team. */
export function useStaffTeamDeletions(options: TeamDeletionsOptions): TeamDeletions {
  const queryKey = getListTeamDeletionsApiInternalTeamDeletionsGetQueryKey()
  const query = useListTeamDeletionsApiInternalTeamDeletionsGet({
    query: {
      staleTime: 0,
      retry: false,
      refetchInterval: (current) => {
        const body = current.state.data
        return nextDeletionsPollMs(body?.status === 200 ? body.data.deletions : undefined)
      },
    },
  })
  const retry = useRetryTeamDeletionApiInternalTeamDeletionsDeletionIdRetryPost({
    mutation: { onSettled: useRefreshAfter(queryKey) },
  })
  const read: Read = {
    records: query.data?.status === 200 ? query.data.data.deletions : undefined,
    at: query.dataUpdatedAt,
  }
  return useDeletions(read, queryKey, retry, options)
}
